interface FileEntry {
  name: string;
  url: string;
  folder: string;
  size: number;
}

export async function createZipFromUrls(files: FileEntry[], zipName = 'solidify_tasks.zip') {
  // CRC32 Table
  const crcTable = (() => {
    const table: number[] = [];
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      }
      table[n] = c;
    }
    return table;
  })();

  const crc32 = (buffer: ArrayBuffer): number => {
    let crc = 0 ^ -1;
    const bytes = new Uint8Array(buffer);
    for (const byte of bytes) {
      crc = (crc >>> 8) ^ crcTable[(crc ^ byte) & 0xff];
    }
    return (crc ^ -1) >>> 0;
  };

  const fetchFile = async (url: string): Promise<ArrayBuffer> => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch ${url}`);
    return response.arrayBuffer();
  };

  // ZIP Structure Creators
  const createLocalFileHeader = (fileName: string, crc: number, compressedSize: number, uncompressedSize: number): Uint8Array => {
    const encoder = new TextEncoder();
    const nameBytes = encoder.encode(fileName);
    const header = new DataView(new ArrayBuffer(30 + nameBytes.length));
    header.setUint32(0, 0x04034b50, true); // Signature
    header.setUint16(4, 0x0014, true); // Version needed
    header.setUint16(6, 0x0800, true); // Flags (UTF-8)
    header.setUint16(8, 0, true); // Compression method
    header.setUint16(10, 0, true); // Mod time
    header.setUint16(12, 0, true); // Mod date
    header.setUint32(14, crc, true); // CRC-32
    header.setUint32(18, compressedSize, true);
    header.setUint32(22, uncompressedSize, true);
    header.setUint16(26, nameBytes.length, true);
    header.setUint16(28, 0, true); // Extra field length
    const headerBytes = new Uint8Array(header.buffer);
    headerBytes.set(nameBytes, 30);
    return headerBytes;
  };
  const createCentralDirectoryEntry = (fileName: string, crc: number, compressedSize: number, uncompressedSize: number, offset: number): Uint8Array => {
    const encoder = new TextEncoder();
    const nameBytes = encoder.encode(fileName);
    const entry = new DataView(new ArrayBuffer(46 + nameBytes.length));
    entry.setUint32(0, 0x02014b50, true); // Signature
    entry.setUint16(4, 0x0014, true); // Version made by
    entry.setUint16(6, 0x0014, true); // Version needed
    entry.setUint16(8, 0x0800, true); // Flags (UTF-8)
    entry.setUint16(10, 0, true); // Compression method
    entry.setUint16(12, 0, true); // Mod time
    entry.setUint16(14, 0, true); // Mod date
    entry.setUint32(16, crc, true); // CRC-32
    entry.setUint32(20, compressedSize, true);
    entry.setUint32(24, uncompressedSize, true);
    entry.setUint16(28, nameBytes.length, true);
    entry.setUint16(30, 0, true); // Extra field length
    entry.setUint16(32, 0, true); // Comment length
    entry.setUint16(34, 0, true); // Disk number
    entry.setUint16(36, 0, true); // Internal attributes
    entry.setUint32(38, 0, true); // External attributes
    entry.setUint32(42, offset, true); // Header offset
    const entryBytes = new Uint8Array(entry.buffer);
    entryBytes.set(nameBytes, 46);
    return entryBytes;
  };
  const createEndOfCentralDirectory = (entriesCount: number, cdSize: number, cdOffset: number): Uint8Array => {
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true); // Signature
    end.setUint16(4, 0, true); // Disk number
    end.setUint16(6, 0, true); // Start disk
    end.setUint16(8, entriesCount, true); // Disk entries
    end.setUint16(10, entriesCount, true); // Total entries
    end.setUint32(12, cdSize, true); // CD size
    end.setUint32(16, cdOffset, true); // CD offset
    end.setUint16(20, 0, true); // Comment length
    return new Uint8Array(end.buffer);
  };
  try {
    const zipParts: Uint8Array[] = [];
    const centralDirectory: Uint8Array[] = [];
    let currentOffset = 0;

    const usedPaths = new Map<string, number>();

    for (const { name, url, folder } of files) {
      const fileData = await fetchFile(url);
      const crc = crc32(fileData);
      const size = fileData.byteLength;

      const basePath = folder ? `${folder}/${name}` : name;
      let fullPath = basePath;

      let count = usedPaths.get(basePath) || 0;
      while (usedPaths.has(fullPath)) {
        count++;
        const extIndex = name.lastIndexOf('.');
        const nameWithoutExt = name.slice(0, extIndex);
        const ext = name.slice(extIndex);
        const newName = `${nameWithoutExt}_${count}${ext}`;
        fullPath = folder ? `${folder}/${newName}` : newName;
      }
      usedPaths.set(basePath, count);
      usedPaths.set(fullPath, 1);

      const header = createLocalFileHeader(fullPath, crc, size, size);
      zipParts.push(header);
      zipParts.push(new Uint8Array(fileData));

      const cdEntry = createCentralDirectoryEntry(fullPath, crc, size, size, currentOffset);
      centralDirectory.push(cdEntry);
      currentOffset += header.byteLength + size;
    }

    const cdSize = centralDirectory.reduce((sum, entry) => sum + entry.byteLength, 0);
    const cdOffset = currentOffset;
    const endRecord = createEndOfCentralDirectory(files.length, cdSize, cdOffset);

    const zipBlob = new Blob([...zipParts, ...centralDirectory, endRecord], { type: 'application/zip' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(zipBlob);
    link.download = zipName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error creating ZIP file:', error);
    throw error;
  }
}
